// The words every page shares: how a result is said (domain/points.ts), the colours, a few counts.
import { words } from "../services/i18n.ts";

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

export const t = words({
    en: {
        pointsOutOfTen: (value: string) => `${value} point${value === "1" ? "" : "s"} out of 10`,
        points: (value: string) => `${value} point${value === "1" ? "" : "s"}`,
        record: (wins: number, draws: number, losses: number) =>
            `${plural(wins, "win", "wins")}, ${plural(draws, "draw", "draws")}, ${plural(losses, "loss", "losses")}`,
        recordShort: (wins: number, draws: number, losses: number) => `${wins}W ${draws}D ${losses}L`,
        games: (n: number) => plural(n, "game", "games"),
        white: "White",
        black: "Black",
        withWhite: "With White",
        withBlack: "With Black",
        next: "Next",
        and: "and",
        today: "today",
        daysAgo: (days: number) => (days === 1 ? "1 day ago" : `${days} days ago`),
        weeksAgo: (weeks: number) => (weeks === 1 ? "1 week ago" : `${weeks} weeks ago`),
        monthsAgo: (months: number) => (months <= 1 ? "1 month ago" : `${months} months ago`),
        weekdays: "Mon,Tue,Wed,Thu,Fri,Sat,Sun",
        months: "Jan,Feb,Mar,Apr,May,Jun,Jul,Aug,Sep,Oct,Nov,Dec",
        dayLabel: (weekday: string, day: number, month: string) => `${weekday} ${day} ${month}`,
        since: (date: string) => `since ${date}`,
        analysis: "Analysis",
        yourProgress: "Your progress",
        won: "Won",
        drawn: "Drawn",
        lost: "Lost",
        daysInARow: "Days in a row",
        practicesToday: (done: number, goal: number) => `${done} of ${goal} practices today`,
        streakDays: (days: number) => `${days} day${days === 1 ? "" : "s"}`,
        belt: (colour: string) => colour,
        // The analysis, in the header while it runs (components/chrome/AnalysisBadge.tsx)
        analysisQueued: "Analysis queued",
        analysisAhead: (n: number) => (n === 0 ? "starting next" : `${n} ahead of you`),
        analysisRunning: (percent: number) => `Analysing · ${percent}%`,
        analysisLeft: (left: string) => `${left} left`,
        analysisReady: "Report ready",
        analysisOpen: "Open your analysis",
    },
    es: {
        pointsOutOfTen: (value: string) => `${value} punto${value === "1" ? "" : "s"} de 10`,
        points: (value: string) => `${value} punto${value === "1" ? "" : "s"}`,
        record: (wins: number, draws: number, losses: number) =>
            `${plural(wins, "victoria", "victorias")}, ${plural(draws, "tablas", "tablas")}, ${plural(losses, "derrota", "derrotas")}`,
        recordShort: (wins: number, draws: number, losses: number) => `${wins}V ${draws}T ${losses}D`,
        games: (n: number) => plural(n, "partida", "partidas"),
        white: "Blancas",
        black: "Negras",
        withWhite: "Con blancas",
        withBlack: "Con negras",
        next: "Siguiente",
        and: "y",
        today: "hoy",
        daysAgo: (days: number) => (days === 1 ? "hace 1 día" : `hace ${days} días`),
        weeksAgo: (weeks: number) => (weeks === 1 ? "hace 1 semana" : `hace ${weeks} semanas`),
        monthsAgo: (months: number) => (months <= 1 ? "hace 1 mes" : `hace ${months} meses`),
        weekdays: "lun,mar,mié,jue,vie,sáb,dom",
        months: "ene,feb,mar,abr,may,jun,jul,ago,sep,oct,nov,dic",
        dayLabel: (weekday: string, day: number, month: string) => `${weekday} ${day} ${month}`,
        since: (date: string) => `desde el ${date}`,
        analysis: "Análisis",
        yourProgress: "Tu progreso",
        won: "Ganadas",
        drawn: "Tablas",
        lost: "Perdidas",
        daysInARow: "Días seguidos",
        practicesToday: (done: number, goal: number) => `${done} de ${goal} prácticas hoy`,
        streakDays: (days: number) => `${days} día${days === 1 ? "" : "s"}`,
        belt: (colour: string) => ({
            "White belt": "Cinturón blanco", "Yellow belt": "Cinturón amarillo", "Orange belt": "Cinturón naranja",
            "Green belt": "Cinturón verde", "Blue belt": "Cinturón azul", "Purple belt": "Cinturón morado",
            "Brown belt": "Cinturón marrón", "Black belt": "Cinturón negro",
        } as Record<string, string>)[colour] ?? colour,
        analysisQueued: "Análisis en cola",
        analysisAhead: (n: number) => (n === 0 ? "eres el siguiente" : `${n} por delante`),
        analysisRunning: (percent: number) => `Analizando · ${percent}%`,
        analysisLeft: (left: string) => `quedan ${left}`,
        analysisReady: "Informe listo",
        analysisOpen: "Abre tu análisis",
    },
});

/** Monday first, three letters or so, in the page's language. */
export const weekdayNames = (): string[] => t("weekdays").split(",");

/** January first, short, in the page's language. */
export const monthNames = (): string[] => t("months").split(",");
